// src/components/Admin/StudentCardRequests.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Snackbar,
  TextField
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const StudentCardRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [rejectDialog, setRejectDialog] = useState({ open: false, requestId: null });
  const [rejectComment, setRejectComment] = useState('');
  const [docBlob, setDocBlob] = useState(null);
  const [docBlobUrl, setDocBlobUrl] = useState(null);
  const [docLoading, setDocLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStudentCardRequests();
      setRequests(response.requests);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch student card requests');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (request) => {
    setSelectedRequest(request);
    setOpenDialog(true);
    if (request.proof_of_loss_path) {
      setDocLoading(true);
      setDocBlobUrl(null);
      try {
        const blob = await adminAPI.getCardDocument(request.id);
        setDocBlob(blob);
        setDocBlobUrl(URL.createObjectURL(blob));
      } catch (_) {
        setDocBlobUrl(null);
      } finally {
        setDocLoading(false);
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRequest(null);
    if (docBlobUrl) { URL.revokeObjectURL(docBlobUrl); setDocBlobUrl(null); setDocBlob(null); }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleUpdateStatus = async (id, status, comment = '') => {
    try {
      setActionLoading(true);
      await adminAPI.updateStudentCardRequestStatus(id, status, comment);
      setNotification({ open: true, message: `Demande ${status === 'approved' ? 'approuvée' : 'rejetée'} avec succès`, severity: 'success' });
      setRequests(requests.map(req => req.id === id ? { ...req, status, comment } : req));
      if (openDialog) handleCloseDialog();
    } catch (err) {
      setNotification({ open: true, message: err.response?.data?.error || `Erreur`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (id) => {
    setRejectComment('');
    setRejectDialog({ open: true, requestId: id });
    if (openDialog) handleCloseDialog();
  };

  const handleConfirmReject = async () => {
    await handleUpdateStatus(rejectDialog.requestId, 'rejected', rejectComment);
    setRejectDialog({ open: false, requestId: null });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="600" color="primary" sx={{ mb: 3 }}>
        Student Card Requests
      </Typography>
      
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} aria-label="student card requests table">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Student Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>CIN</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Request Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length > 0 ? (
              requests.map((request, index) => (
                // FIX: Use composite key or fallback to index to avoid "duplicate key" warning
                <TableRow key={`${request.id}-${index}`} hover>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>#{request.id}</TableCell>
                  <TableCell>{`${request.lib_nom_pat_ind} ${request.lib_pr1_ind}`}</TableCell>
                  <TableCell>{request.cod_etu}</TableCell>
                  <TableCell>{request.cin_ind}</TableCell>
                  <TableCell>
                    <Chip 
                      label={request.status} 
                      color={
                        request.status === 'approved' ? 'success' : 
                        request.status === 'rejected' ? 'error' : 'warning'
                      }
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton 
                          color="info" 
                          size="small" 
                          onClick={() => handleOpenDialog(request)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {request.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton 
                              color="success" 
                              size="small"
                              onClick={() => handleUpdateStatus(request.id, 'approved')}
                              disabled={actionLoading}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rejeter">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => openRejectDialog(request.id)}
                              disabled={actionLoading}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No student card requests found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedRequest && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Request Details
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Justificatif de perte</Typography>
                  {selectedRequest.proof_of_loss_path ? (
                    <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden', textAlign: 'center', p: 1 }}>
                      {docLoading && <CircularProgress size={32} sx={{ my: 2 }} />}
                      {!docLoading && docBlobUrl && (
                        <>
                          <Box
                            component="img"
                            src={docBlobUrl}
                            alt="Justificatif"
                            sx={{ maxWidth: '100%', maxHeight: 350, objectFit: 'contain', display: 'block', margin: '0 auto' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => {
                              const mimeToExt = { 'image/jpeg': '.jpg', 'image/png': '.png', 'application/pdf': '.pdf', 'image/gif': '.gif', 'image/webp': '.webp' };
                              const ext = mimeToExt[docBlob?.type] || '';
                              const a = document.createElement('a');
                              a.href = docBlobUrl;
                              a.download = `justificatif_${selectedRequest.cod_etu}${ext}`;
                              a.click();
                            }}
                          >
                            Télécharger le document
                          </Button>
                        </>
                      )}
                      {!docLoading && !docBlobUrl && (
                        <Typography variant="caption" color="error">Impossible de charger le document</Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ height: 80, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Aucun document joint</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {`${selectedRequest.lib_nom_pat_ind} ${selectedRequest.lib_pr1_ind}`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Student Code</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedRequest.cod_etu}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">CIN</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedRequest.cin_ind}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date Requested</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {new Date(selectedRequest.created_at).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDialog} color="inherit">Close</Button>
              {selectedRequest.status === 'pending' && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => openRejectDialog(selectedRequest.id)}
                    disabled={actionLoading}
                  >
                    Rejeter
                  </Button>
                  <Button 
                    variant="contained" 
                    color="success"
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'approved')}
                    disabled={actionLoading}
                  >
                    Approve
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject with comment dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, requestId: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Motif du rejet
          <IconButton size="small" onClick={() => setRejectDialog({ open: false, requestId: null })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Veuillez indiquer la raison du rejet. Ce message sera visible par l'étudiant.
          </Typography>
          <TextField
            label="Motif du rejet"
            multiline
            rows={3}
            fullWidth
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Ex: Document illisible, justificatif insuffisant..."
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog({ open: false, requestId: null })} color="inherit">Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={actionLoading || !rejectComment.trim()}
          >
            {actionLoading ? 'En cours...' : 'Confirmer le rejet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentCardRequests;